/**
 * Phase 5 batch 1 Slice 3 — Firebase Auth client.
 *
 * Wraps Firebase Auth's modular SDK in a thin, app-friendly façade
 * tailored to the Phase 5 plan's confirmed decisions:
 *   - Email/password (with mandatory verification before intake) and
 *     Google sign-in (popup). Apple deferred. Magic-link rejected.
 *   - `subscribe(listener)` wraps `onIdTokenChanged` (NOT
 *     `onAuthStateChanged`) so refresh-token expiry triggers a
 *     re-auth event in long-running tabs, per Phase 5 plan risk row.
 *
 * The client exposes raw Firebase `User` objects to callers; the
 * applicant-web app composes the AuthGate on top, including the
 * "verify your email" interstitial.
 *
 * Source-cited Firebase APIs (verified against
 * `node_modules/firebase/node_modules/@firebase/auth/dist/auth.d.ts`):
 *   - `getAuth(app)` -> Auth
 *   - `createUserWithEmailAndPassword(auth, email, password)`
 *   - `signInWithEmailAndPassword(auth, email, password)`
 *   - `signInWithPopup(auth, GoogleAuthProvider)` -> UserCredential
 *   - `signOut(auth)` -> Promise<void>
 *   - `sendEmailVerification(user)` -> Promise<void>
 *   - `onIdTokenChanged(auth, observer)` -> Unsubscribe
 *
 * https://firebase.google.com/docs/auth/web/start
 * https://firebase.google.com/docs/auth/web/google-signin
 * https://firebase.google.com/docs/auth/web/manage-users
 *
 * Common error codes the UI surfaces (per Firebase docs and the
 * `AuthErrorCodes` const exported from firebase/auth):
 *   - 'auth/email-already-in-use'  — sign-up of an existing email
 *   - 'auth/invalid-email'         — malformed email
 *   - 'auth/weak-password'         — < 6 chars
 *   - 'auth/wrong-password'        — invalid sign-in credentials
 *   - 'auth/user-not-found'        — email never registered
 *   - 'auth/too-many-requests'     — rate-limit cooldown
 *   - 'auth/popup-closed-by-user'  — Google popup dismissed
 *   - 'auth/network-request-failed' — offline / DNS / CORS issue
 *
 * The applicant-web's sign-in form maps these to user-facing copy.
 */

import {
  type Auth,
  type User,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';

export type AuthListener = (user: User | null) => void;
export type AuthUnsubscribe = () => void;

export class EmailNotVerifiedError extends Error {
  readonly code = 'auth/email-not-verified';
  constructor(public readonly user: User) {
    super(
      `Email address ${user.email ?? '(unknown)'} has not been verified. ` +
        `Click the link in the verification email and try again.`,
    );
    this.name = 'EmailNotVerifiedError';
  }
}

export interface AuthClient {
  /**
   * Creates an account and immediately sends a verification email.
   * The returned user is **not** intake-eligible until they click the
   * link; the security rules enforce this independently.
   */
  signUpWithEmail: (email: string, password: string) => Promise<User>;

  /**
   * Signs in with email/password. Throws `EmailNotVerifiedError` when
   * the user has not yet clicked the verification link, so the UI can
   * fail fast (the rules enforce the same gate server-side).
   */
  signInWithEmail: (email: string, password: string) => Promise<User>;

  /**
   * Google sign-in via popup. Google credentials are implicitly
   * verified — the verification gate does not apply.
   */
  signInWithGoogle: () => Promise<User>;

  /** Signs out the current user; clears the auth token cache. */
  signOut: () => Promise<void>;

  /**
   * Subscribes to ID-token changes. Fires on sign-in, sign-out, and
   * (critically) on refresh-token expiry — wrapping `onIdTokenChanged`
   * lets long-lived tabs detect auth-failure transitions
   * `onAuthStateChanged` would miss.
   */
  subscribe: (listener: AuthListener) => AuthUnsubscribe;

  /** Returns the currently signed-in user, or null. */
  getCurrentUser: () => User | null;

  /**
   * Re-sends the verification email to the currently signed-in user.
   * Throws if no user is signed in or if the user is already verified.
   */
  resendVerificationEmail: () => Promise<void>;
}

export interface IAuthClientOptions {
  /**
   * Where to send the user after they click the verification link and
   * our custom handler page completes `applyActionCode`. Passed as
   * `actionCodeSettings.url` on every `sendEmailVerification` call so
   * Firebase appends it as the `continueUrl` query param on the email
   * link. Our `<VerifyEmailHandlerPage>` reads `continueUrl` from
   * the URL and redirects there on success.
   *
   * Optional for back-compat with tests + the legacy default-handler
   * flow. Production deployments should always set this so users
   * land in our UI (not Firebase's hosted page) after verification.
   */
  verifyEmailContinueUrl?: string;
}

/**
 * Builds an `AuthClient` against the supplied Firebase `Auth`
 * instance. The caller owns SDK initialization (`getAuth(app)`).
 */
export const createAuthClient = (
  auth: Auth,
  options: IAuthClientOptions = {},
): AuthClient => {
  // Lazy-built per call so each invocation can carry the same
  // settings reference. `undefined` when no continue URL is configured
  // — `sendEmailVerification` accepts the bare `(user)` signature in
  // that case (legacy + test behavior).
  const buildActionCodeSettings = ():
    | { url: string; handleCodeInApp: false }
    | undefined => {
    const url = options.verifyEmailContinueUrl;
    if (url === undefined || url.length === 0) return undefined;
    // `handleCodeInApp: false` keeps the link routing through Firebase's
    // hosted action URL (overridden by the per-template "Customize
    // action URL" in the Console), so our handler page receives the
    // standard `mode=verifyEmail&oobCode=...&continueUrl=...` shape.
    // Setting `true` would force a deep-link/dynamic-link flow we
    // don't need for web-only auth.
    return { url, handleCodeInApp: false };
  };

  const sendVerificationEmailWithSettings = (user: User): Promise<void> => {
    const settings = buildActionCodeSettings();
    return settings === undefined
      ? sendEmailVerification(user)
      : sendEmailVerification(user, settings);
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
  ): Promise<User> => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    // Verification is mandatory per Phase 5 plan; sending here means
    // the user lands at the "verify your email" interstitial instead
    // of the intake form.
    await sendVerificationEmailWithSettings(cred.user);
    return cred.user;
  };

  const signInWithEmail = async (
    email: string,
    password: string,
  ): Promise<User> => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    if (!cred.user.emailVerified) {
      throw new EmailNotVerifiedError(cred.user);
    }
    return cred.user;
  };

  const signInWithGoogle = async (): Promise<User> => {
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    return cred.user;
  };

  const signOut = async (): Promise<void> => {
    await firebaseSignOut(auth);
  };

  const subscribe = (listener: AuthListener): AuthUnsubscribe => {
    return onIdTokenChanged(auth, (user) => {
      try {
        listener(user);
      } catch (err) {
        const error = err;
        queueMicrotask(() => {
          throw error;
        });
      }
    });
  };

  const getCurrentUser = (): User | null => auth.currentUser;

  const resendVerificationEmail = async (): Promise<void> => {
    const user = auth.currentUser;
    if (user === null) {
      throw new Error('Cannot resend verification email — no user signed in.');
    }
    if (user.emailVerified) {
      throw new Error(
        `User ${user.email ?? '(unknown)'} is already verified.`,
      );
    }
    await sendVerificationEmailWithSettings(user);
  };

  return {
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    signOut,
    subscribe,
    getCurrentUser,
    resendVerificationEmail,
  };
};
